import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { formatDate, formatCurrency, getPaymentStatusLabel, getPaymentStatusColor, isPaymentPaid } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import TripWizardModal from '../../components/TripWizard/TripWizardModal';
import QRCodeModal from '../../components/QRCodeModal';
import { Trip } from '../../types';
import { 
  Plane, 
  Calendar, 
  PlusCircle, 
  MapPin,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Eye,
  QrCode
} from 'lucide-react';

const TripsPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    trips, 
    loading,
    error,
    refreshTrips
  } = useApp();

  const [isTripWizardOpen, setIsTripWizardOpen] = useState(false);
  const [qrCodeModal, setQrCodeModal] = useState<{
    isOpen: boolean;
    voucherCode: string;
    participantName?: string;
  }>({
    isOpen: false,
    voucherCode: '',
    participantName: ''
  });

  // Safe data access with fallbacks
  const safeTrips = trips || [];
  const isLoading = loading.trips;

  // Separar viagem atual e outras viagens
  const currentTrip = safeTrips.find(trip => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    return startDate <= now && now <= endDate;
  });

  const upcomingTrips = safeTrips.filter(trip => {
    const startDate = new Date(trip.start_date);
    return startDate > new Date();
  }).sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

  const pastTrips = safeTrips.filter(trip => {
    const endDate = new Date(trip.end_date);
    return endDate < new Date();
  }).sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime());

  const handleTripSuccess = (tripId: number) => {
    console.log('✅ Trip created successfully:', tripId);
    refreshTrips();
  };

  const handleViewDetails = (tripId: number) => {
    navigate(`/trips/${tripId}`);
  };

  const handleViewQRCode = (voucherCode: string, participantName?: string) => {
    setQrCodeModal({
      isOpen: true,
      voucherCode,
      participantName
    });
  };

  const getTripStatusInfo = (trip: Trip) => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    if (startDate <= now && now <= endDate) {
      return { status: 'current', label: 'Em andamento', color: 'bg-blue-100 text-blue-800', icon: Clock };
    } else if (startDate > now) {
      return { status: 'upcoming', label: 'Próxima', color: 'bg-green-100 text-green-800', icon: Calendar };
    } else {
      return { status: 'past', label: 'Finalizada', color: 'bg-gray-100 text-gray-800', icon: CheckCircle };
    }
  };

  const TripCard: React.FC<{ trip: Trip; isHighlighted?: boolean; showQRCode?: boolean }> = ({ 
    trip, 
    isHighlighted = false, 
    showQRCode = true 
  }) => {
    const statusInfo = getTripStatusInfo(trip);
    const StatusIcon = statusInfo.icon;
    const isPaid = isPaymentPaid(trip.payment_status_id);
    
    return (
      <div className={`bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-xl hover:scale-105 ${
        isHighlighted ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
      }`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{trip.hotel_name}</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <StatusIcon className="h-4 w-4" />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                isPaid ? 'bg-green-100 text-green-800' : getPaymentStatusColor(trip.payment_status_id)
              }`}>
                {isPaid ? <CheckCircle className="h-4 w-4 mr-1" /> : <AlertCircle className="h-4 w-4 mr-1" />}
                {getPaymentStatusLabel(trip.payment_status_id)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                <Calendar className="h-4 w-4" />
                <span>Período</span>
              </div>
              <p className="font-medium text-gray-900">
                {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
              </p>
              <p className="text-xs text-gray-500">{trip.total_days} dias</p>
            </div>
            
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                <Users className="h-4 w-4" />
                <span>Participantes</span>
              </div>
              <p className="font-medium text-gray-900">{trip.participants.length}</p>
              <p className="text-xs text-gray-500">
                {trip.participants.filter(p => p.is_exempt).length} isentos
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <CreditCard className="h-4 w-4" />
                <span>Total:</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(trip.total_amount)}</p>
            </div>
            
            <div className="flex space-x-2">
              {showQRCode && isPaid && trip.voucher_code && (
                <button
                  onClick={() => handleViewQRCode(trip.voucher_code || '', trip.user?.name)}
                  className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  <QrCode className="h-4 w-4 mr-1" />
                  QR Code
                </button>
              )}
              <button
                onClick={() => handleViewDetails(trip.id)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <Eye className="h-4 w-4 mr-2" />
                Ver Detalhes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" customText="Carregando suas viagens..." />
        </div>
      </div>
    );
  }

  if (error?.trips) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert type="error" title="Erro ao carregar viagens">
            {error.trips}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Minhas Viagens</h1>
            <p className="text-gray-600">
              Gerencie suas viagens para Jericoacoara
            </p>
          </div>
          
          <button
            onClick={() => setIsTripWizardOpen(true)}
            className="mt-4 sm:mt-0 inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <PlusCircle className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Nova Viagem</span>
          </button>
        </div>

        {/* Viagem Atual */}
        {currentTrip && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Clock className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Viagem Atual</h2>
            </div>
            <TripCard trip={currentTrip} isHighlighted={true} />
          </div>
        )}

        {/* Próximas Viagens */}
        {upcomingTrips.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="h-5 w-5 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Próximas Viagens</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingTrips.map((trip) => (
                <TripCard key={trip.id} trip={trip} />
              ))}
            </div>
          </div>
        )}

        {/* Viagens Passadas */}
        {pastTrips.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle className="h-5 w-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Viagens Realizadas</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastTrips.map((trip) => (
                <TripCard key={trip.id} trip={trip} showQRCode={false} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {safeTrips.length === 0 && (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
              <Plane className="h-12 w-12 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhuma viagem encontrada</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Você ainda não tem viagens cadastradas. Que tal planejar sua primeira aventura em Jericoacoara?
            </p>
            <button
              onClick={() => setIsTripWizardOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <PlusCircle className="h-5 w-5 sm:mr-2" />
              <span className="hidden sm:inline">Criar Primeira Viagem</span>
            </button>
          </div>
        )}

        {/* Trip Wizard Modal */}
        <TripWizardModal
          isOpen={isTripWizardOpen}
          onClose={() => setIsTripWizardOpen(false)}
          onSuccess={handleTripSuccess}
        />

        {/* QR Code Modal */}
        <QRCodeModal
          isOpen={qrCodeModal.isOpen}
          onClose={() => setQrCodeModal({ isOpen: false, voucherCode: '', participantName: '' })}
          voucherCode={qrCodeModal.voucherCode}
          participantName={qrCodeModal.participantName}
        />
      </div>
    </div>
  );
};

export default TripsPage;
